import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, forkJoin } from 'rxjs';
import { TenantRoleWizardComponent } from '../../modals/tenant-role-wizard/tenant-role-wizard.component';
import { LoaderComponent } from '../../common/loader/loader.component';
import { TenantRolesService } from '../../services/tenant-roles.service';
import { ToastrService } from '../../services/toastr.service';
import {
  AclPage,
  AclRole,
  AclRoleDetail,
  BlobPermissionInput,
  DbPermissionInput,
  RolePageAssignment,
  RolePermission,
  TableColumnsEntry
} from '../../models/tenant-role.model';
import { ApiErrorResponse } from '../../models/auth.model';

interface PermItem {
  name: string;
  selected: boolean;
}

interface PageItem extends PermItem {
  id: number;
}

interface SchemaItem extends PermItem {
  availableTables: string[];
  selectedTables: string[];
}

type GroupKey = 'schemas' | 'containers' | 'pages';

@Component({
  selector: 'app-tenant-role-datastream',
  imports: [FormsModule, TenantRoleWizardComponent, LoaderComponent],
  templateUrl: './tenant-role-datastream.component.html',
  styleUrl: './tenant-role-datastream.component.scss'
})
export class TenantRoleDatastreamComponent implements OnInit {
  private tenantUid: string;

  roles: AclRole[] = [];
  rolesLoading = true;

  selectedRoleId: number | null = null;
  selectedRoleDetail: AclRoleDetail | null = null;

  catalogsLoading = true;

  private pagesCatalog: AclPage[] = [];
  private containersCatalog: string[] = [];
  private schemaCatalog: { name: string; tables: string[] }[] = [];

  loading: Record<GroupKey, boolean> = { schemas: false, containers: false, pages: false };
  saving: Record<GroupKey, boolean> = { schemas: false, containers: false, pages: false };

  schemas: SchemaItem[] = [];
  containers: PermItem[] = [];
  pages: PageItem[] = [];

  simpleGroups: { key: 'containers' | 'pages'; title: string; icon: string }[] = [
    { key: 'containers', title: 'Containers', icon: 'bi-box-seam' },
    { key: 'pages', title: 'Pages', icon: 'bi-window' }
  ];

  activeSchema: SchemaItem | null = null;
  pickerSearch = '';
  pickerDraft: string[] = [];

  isAddRoleOpen = false;
  creatingRole = false;
  newRoleName = '';
  newRoleDescription = '';
  newRoleSchemas: SchemaItem[] = [];
  newRoleContainers: PermItem[] = [];
  newRolePages: PageItem[] = [];

  isEditRoleOpen = false;
  savingRoleEdit = false;
  editRoleName = '';
  editRoleDescription = '';

  isDeleteRoleOpen = false;
  deletingRole = false;

  constructor(
    private route: ActivatedRoute,
    private tenantRolesService: TenantRolesService,
    private toastrService: ToastrService
  ) {
    this.tenantUid = this.route.snapshot.paramMap.get('slug') ?? '';
  }

  ngOnInit(): void {
    this.loadCatalogs();
    this.loadRoles();
  }

  get selectedRole(): AclRole | null {
    return this.roles.find(r => r.id === this.selectedRoleId) ?? null;
  }

  private loadCatalogs(): void {
    this.catalogsLoading = true;

    forkJoin({
      pages: this.tenantRolesService.getPages(true),
      containers: this.tenantRolesService.getContainers(this.tenantUid),
      tablesColumns: this.tenantRolesService.getTablesColumns(this.tenantUid)
    }).subscribe({
      next: ({ pages, containers, tablesColumns }) => {
        this.pagesCatalog = pages;
        this.containersCatalog = containers.map(c => c.name);
        this.schemaCatalog = this.buildSchemaCatalog(tablesColumns.tables);
        this.catalogsLoading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.catalogsLoading = false;
        this.toastrService.error(this.resolveError(err, 'Unable to load permission catalogs.'));
      }
    });
  }

  private loadRoles(): void {
    this.rolesLoading = true;

    this.tenantRolesService.getRoles(this.tenantUid).subscribe({
      next: (roles) => {
        this.roles = roles;
        this.rolesLoading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.rolesLoading = false;
        this.toastrService.error(this.resolveError(err, 'Unable to load roles.'));
      }
    });
  }

  private buildSchemaCatalog(tables: TableColumnsEntry[]): { name: string; tables: string[] }[] {
    const map = new Map<string, string[]>();
    for (const t of tables) {
      const dotIdx = t.table_name.indexOf('.');
      const schema = dotIdx > -1 ? t.table_name.slice(0, dotIdx) : 'default';
      const table = dotIdx > -1 ? t.table_name.slice(dotIdx + 1) : t.table_name;
      if (!map.has(schema)) {
        map.set(schema, []);
      }
      map.get(schema)!.push(table);
    }
    return Array.from(map.entries()).map(([name, tables]) => ({ name, tables }));
  }

  private buildSchemas(permissions?: RolePermission[]): SchemaItem[] {
    const dbPerms = (permissions ?? []).filter(p => p.resource_type === 'db');
    const hasWildcardAccess = dbPerms.some(p => p.schema_name === '*' && p.permission_type === 'schema');

    return this.schemaCatalog.map(s => {
      if (hasWildcardAccess) {
        return { name: s.name, availableTables: s.tables, selected: true, selectedTables: [] };
      }

      const matching = dbPerms.filter(p => this.matchesSchema(p.schema_name, s.name));
      const fullAccess = matching.some(p => p.permission_type === 'schema');
      const selectedTables = fullAccess
        ? []
        : matching.filter(p => p.permission_type === 'table').map(p => this.extractTableName(p, s.name, s.tables));

      return {
        name: s.name,
        availableTables: s.tables,
        selected: matching.length > 0,
        selectedTables
      };
    });
  }

  private matchesSchema(schemaName: string | null, targetSchema: string): boolean {
    if (!schemaName) return false;
    const lower = schemaName.toLowerCase();
    const targetLower = targetSchema.toLowerCase();
    return lower === targetLower || lower.startsWith(`${targetLower}.`);
  }

  private extractTableName(perm: RolePermission, schemaName: string, availableTables: string[]): string {
    const prefix = `${schemaName}.`;
    const parsed = perm.resource_path.toLowerCase().startsWith(prefix.toLowerCase())
      ? perm.resource_path.slice(prefix.length)
      : perm.resource_path;
    const normalized = availableTables.find(t => t.toLowerCase() === parsed.toLowerCase());
    return normalized ?? parsed;
  }

  private buildContainers(permissions?: RolePermission[]): PermItem[] {
    const blobPerms = (permissions ?? []).filter(p => p.resource_type === 'blob');
    const hasWildcardAccess = blobPerms.some(p => p.container_name === '*');
    const selectedNames = new Set(blobPerms.map(p => p.container_name));

    return this.containersCatalog.map(name => ({
      name,
      selected: hasWildcardAccess || selectedNames.has(name)
    }));
  }

  private buildPages(assignments?: RolePageAssignment[]): PageItem[] {
    const selectedIds = new Set((assignments ?? []).map(a => a.page));
    return this.pagesCatalog.map(p => ({ id: p.id, name: p.display_name || p.name, selected: selectedIds.has(p.id) }));
  }

  selectRole(roleId: number): void {
    if (this.catalogsLoading) {
      return;
    }

    this.selectedRoleId = roleId;
    this.selectedRoleDetail = null;
    (['schemas', 'containers', 'pages'] as GroupKey[]).forEach(key => (this.loading[key] = true));

    this.tenantRolesService.getRole(roleId).subscribe({
      next: (detail) => {
        this.selectedRoleDetail = detail;
        this.schemas = this.buildSchemas(detail.permissions);
        this.containers = this.buildContainers(detail.permissions);
        this.loading.schemas = false;
        this.loading.containers = false;
      },
      error: (err: HttpErrorResponse) => {
        this.loading.schemas = false;
        this.loading.containers = false;
        this.toastrService.error(this.resolveError(err, 'Unable to load role permissions.'));
      }
    });

    this.tenantRolesService.getPageAssignments(roleId).subscribe({
      next: (assignments) => {
        this.pages = this.buildPages(assignments);
        this.loading.pages = false;
      },
      error: (err: HttpErrorResponse) => {
        this.loading.pages = false;
        this.toastrService.error(this.resolveError(err, 'Unable to load role page assignments.'));
      }
    });
  }

  itemsFor(group: GroupKey): PermItem[] {
    return this[group];
  }

  isAllSelected(group: GroupKey): boolean {
    const items = this.itemsFor(group);
    return items.length > 0 && items.every(i => i.selected);
  }

  isIndeterminate(group: GroupKey): boolean {
    const items = this.itemsFor(group);
    return items.some(i => i.selected) && !this.isAllSelected(group);
  }

  toggleSelectAll(group: GroupKey): void {
    const makeSelected = !this.isAllSelected(group);
    this.itemsFor(group).forEach(i => (i.selected = makeSelected));
  }

  isListAllSelected(items: PermItem[]): boolean {
    return items.length > 0 && items.every(i => i.selected);
  }

  toggleListSelectAll(items: PermItem[]): void {
    const makeSelected = !this.isListAllSelected(items);
    items.forEach(i => (i.selected = makeSelected));
  }

  saveGroup(group: GroupKey): void {
    if (!this.selectedRoleId) {
      return;
    }
    this.saving[group] = true;

    if (group === 'pages') {
      const pageIds = this.pages.filter(p => p.selected).map(p => p.id);
      this.tenantRolesService.bulkSetPageAssignments({
        role: this.selectedRoleId,
        pages: pageIds,
        tenant_uid: this.tenantUid
      }).subscribe({
        next: () => {
          this.saving.pages = false;
          this.toastrService.success('Page permissions saved successfully.');
        },
        error: (err: HttpErrorResponse) => this.handleSaveError(group, err)
      });
      return;
    }

    if (group === 'schemas') {
      const dbPermissions: DbPermissionInput[] = this.schemas
        .filter(s => s.selected)
        .map(s => this.toDbPermissionInput(s));

      this.tenantRolesService.bulkSetPermissions({
        role: this.selectedRoleId,
        tenant_uid: this.tenantUid,
        db_permissions: dbPermissions
      }).subscribe({
        next: () => {
          this.saving.schemas = false;
          this.toastrService.success('Schema permissions saved successfully.');
        },
        error: (err: HttpErrorResponse) => this.handleSaveError(group, err)
      });
      return;
    }

    const blobPermissions: BlobPermissionInput[] = this.containers.filter(c => c.selected).map(c => ({ container: c.name }));

    this.tenantRolesService.bulkSetPermissions({
      role: this.selectedRoleId,
      tenant_uid: this.tenantUid,
      blob_permissions: blobPermissions
    }).subscribe({
      next: () => {
        this.saving.containers = false;
        this.toastrService.success('Container permissions saved successfully.');
      },
      error: (err: HttpErrorResponse) => this.handleSaveError(group, err)
    });
  }

  private handleSaveError(group: GroupKey, err: HttpErrorResponse): void {
    this.saving[group] = false;
    this.toastrService.error(this.resolveError(err, 'Unable to save changes.'));
  }

  // Every table in the schema is individually checked - treat it as full schema access,
  // same as when no tables are picked at all, instead of listing each table out.
  private isFullTableAccess(schema: SchemaItem): boolean {
    return schema.selectedTables.length === 0
      || (schema.availableTables.length > 0 && schema.selectedTables.length === schema.availableTables.length);
  }

  private toDbPermissionInput(schema: SchemaItem): DbPermissionInput {
    return this.isFullTableAccess(schema) ? { schema: schema.name } : { schema: schema.name, tables: schema.selectedTables };
  }

  schemaBadge(schema: SchemaItem): string {
    return this.isFullTableAccess(schema) ? 'All tables' : `${schema.selectedTables.length} tables selected`;
  }

  openPicker(schema: SchemaItem): void {
    this.activeSchema = schema;
    this.pickerDraft = [...schema.selectedTables];
    this.pickerSearch = '';
  }

  filteredPickerTables(): string[] {
    if (!this.activeSchema) return [];
    const term = this.pickerSearch.toLowerCase();
    return this.activeSchema.availableTables.filter(t => t.toLowerCase().includes(term));
  }

  isPickerTableSelected(table: string): boolean {
    return this.pickerDraft.includes(table);
  }

  togglePickerTable(table: string): void {
    this.pickerDraft = this.isPickerTableSelected(table)
      ? this.pickerDraft.filter(t => t !== table)
      : [...this.pickerDraft, table];
  }

  isPickerAllSelected(): boolean {
    const filtered = this.filteredPickerTables();
    return filtered.length > 0 && filtered.every(t => this.pickerDraft.includes(t));
  }

  togglePickerSelectAll(): void {
    const filtered = this.filteredPickerTables();
    this.pickerDraft = this.isPickerAllSelected()
      ? this.pickerDraft.filter(t => !filtered.includes(t))
      : [...new Set([...this.pickerDraft, ...filtered])];
  }

  applyPicker(): void {
    if (this.activeSchema) {
      this.activeSchema.selectedTables = [...this.pickerDraft];
    }
    this.activeSchema = null;
  }

  cancelPicker(): void {
    this.activeSchema = null;
  }

  openAddRole(): void {
    this.newRoleName = '';
    this.newRoleDescription = '';
    this.newRoleSchemas = this.buildSchemas();
    this.newRoleContainers = this.buildContainers();
    this.newRolePages = this.buildPages();
    this.isAddRoleOpen = true;
  }

  closeAddRole(): void {
    if (this.creatingRole) return;
    this.isAddRoleOpen = false;
  }

  get canSubmitNewRole(): boolean {
    return !!this.newRoleName.trim();
  }

  submitNewRole(): void {
    if (!this.canSubmitNewRole || this.creatingRole) return;

    this.creatingRole = true;

    this.tenantRolesService.createRole({
      name: this.newRoleName.trim(),
      description: this.newRoleDescription.trim() || undefined,
      tenant_uid: this.tenantUid
    }).subscribe({
      next: (role) => this.provisionNewRolePermissions(role),
      error: (err: HttpErrorResponse) => {
        this.creatingRole = false;
        this.toastrService.error(this.resolveError(err, 'Unable to create role.'));
      }
    });
  }

  private provisionNewRolePermissions(role: AclRole): void {
    const pageIds = this.newRolePages.filter(p => p.selected).map(p => p.id);
    const dbPermissions: DbPermissionInput[] = this.newRoleSchemas
      .filter(s => s.selected)
      .map(s => this.toDbPermissionInput(s));
    const blobPermissions: BlobPermissionInput[] = this.newRoleContainers.filter(c => c.selected).map(c => ({ container: c.name }));

    const calls: Observable<unknown>[] = [];
    if (pageIds.length > 0) {
      calls.push(this.tenantRolesService.bulkSetPageAssignments({ role: role.id, pages: pageIds, tenant_uid: this.tenantUid }));
    }
    if (dbPermissions.length > 0 || blobPermissions.length > 0) {
      calls.push(this.tenantRolesService.bulkSetPermissions({
        role: role.id,
        tenant_uid: this.tenantUid,
        ...(dbPermissions.length > 0 ? { db_permissions: dbPermissions } : {}),
        ...(blobPermissions.length > 0 ? { blob_permissions: blobPermissions } : {})
      }));
    }

    const finish = (): void => {
      this.creatingRole = false;
      this.roles = [...this.roles, role];
      this.isAddRoleOpen = false;
      this.toastrService.success(`Role "${role.name}" created successfully.`);
      this.selectRole(role.id);
    };

    if (calls.length === 0) {
      finish();
      return;
    }

    forkJoin(calls).subscribe({
      next: () => finish(),
      error: (err: HttpErrorResponse) => {
        this.creatingRole = false;
        this.isAddRoleOpen = false;
        this.roles = [...this.roles, role];
        this.toastrService.error(this.resolveError(err, 'Role was created, but some permissions failed to save. Open the role to retry.'));
        this.selectRole(role.id);
      }
    });
  }

  openEditRole(): void {
    if (!this.selectedRole) return;
    this.editRoleName = this.selectedRole.name;
    this.editRoleDescription = this.selectedRole.description ?? '';
    this.isEditRoleOpen = true;
  }

  closeEditRole(): void {
    if (this.savingRoleEdit) return;
    this.isEditRoleOpen = false;
  }

  get canSubmitRoleEdit(): boolean {
    return !!this.editRoleName.trim();
  }

  submitRoleEdit(): void {
    if (!this.selectedRoleId || !this.canSubmitRoleEdit || this.savingRoleEdit) return;

    this.savingRoleEdit = true;

    this.tenantRolesService.updateRole(this.selectedRoleId, {
      name: this.editRoleName.trim(),
      description: this.editRoleDescription.trim()
    }).subscribe({
      next: (updated) => {
        this.savingRoleEdit = false;
        this.isEditRoleOpen = false;
        this.roles = this.roles.map(r => (r.id === updated.id ? updated : r));
        this.toastrService.success('Role updated successfully.');
      },
      error: (err: HttpErrorResponse) => {
        this.savingRoleEdit = false;
        this.toastrService.error(this.resolveError(err, 'Unable to update role.'));
      }
    });
  }

  openDeleteRole(): void {
    if (!this.selectedRole) return;
    this.isDeleteRoleOpen = true;
  }

  closeDeleteRole(): void {
    if (this.deletingRole) return;
    this.isDeleteRoleOpen = false;
  }

  confirmDeleteRole(): void {
    if (!this.selectedRoleId || this.deletingRole) return;

    this.deletingRole = true;
    const deletedId = this.selectedRoleId;
    const deletedName = this.selectedRole?.name;

    this.tenantRolesService.deleteRole(deletedId).subscribe({
      next: () => {
        this.deletingRole = false;
        this.isDeleteRoleOpen = false;
        this.roles = this.roles.filter(r => r.id !== deletedId);
        this.selectedRoleId = null;
        this.selectedRoleDetail = null;
        this.schemas = [];
        this.containers = [];
        this.pages = [];
        this.toastrService.success(`Role "${deletedName}" removed successfully.`);
      },
      error: (err: HttpErrorResponse) => {
        this.deletingRole = false;
        this.toastrService.error(this.resolveError(err, 'Unable to remove role.'));
      }
    });
  }

  private resolveError(err: HttpErrorResponse, fallback: string): string {
    const body = err?.error as ApiErrorResponse | undefined;
    if (body?.detail) {
      return body.detail;
    }
    if (body?.error?.message) {
      return body.error.message;
    }
    if (body) {
      for (const key of Object.keys(body)) {
        if (key === 'detail' || key === 'error') continue;
        const value = (body as Record<string, unknown>)[key];
        if (Array.isArray(value) && value.length) {
          return `${key}: ${value[0]}`;
        }
        if (typeof value === 'string') {
          return `${key}: ${value}`;
        }
      }
    }
    return fallback;
  }
}
